import { CCol, CRow } from '@coreui/react';
import GaugeChart from './GaugeChart';
import { HorizontalBarChartNOC } from './HorizontalBarChartNOC';
import { LineChart } from './LineChart';
import { PieChart } from './PieChart';
import { RingGaugeChart } from './RingGaugeChart';
import { SmoothLineChart } from './SmoothLineChart';
import { TotalPowerChart } from './TotalPowerChart';
import { VerticalBarChartNOC } from './VerticalBarChartNOC';

function formatUPSData(rawData) {
  if (!rawData) return [];
  // Lọc dữ liệu chỉ lấy các thiết bị UPS
  const upsData = rawData.filter((item) => item.hotspot_id.startsWith('ups'));

  // Format lại dữ liệu
  return upsData.map((item) => {
    // Chuyển đổi mảng "data" thành object với cặp key-value
    const parsedData = JSON.parse(item.data);
    const formattedData = {};

    // Bắt đầu từ dòng thứ hai trong mảng, vì dòng đầu là tiêu đề
    for (let i = 1; i < parsedData.length; i++) {
      // eslint-disable-next-line no-unused-vars
      const [_, key, value] = parsedData[i]; // Bỏ cột đầu tiên (null hoặc mã thiết bị)
      if (key && value !== undefined) {
        formattedData[key] = value;
      }
    }

    return {
      hotspot_id: item.hotspot_id,
      name: item.name,
      data: formattedData,
    };
  });
}

const getChartData = (data) => {
  if (!data) return [];
  const formattedData = data.map((item) => {
    const rawData = JSON.parse(item.data); // Parse chuỗi data thành mảng
    const formatted = { name: item.name };

    // Gắn từng tiêu chí giám sát vào đối tượng
    rawData.slice(1).forEach((row) => {
      if (row[1] && row[2] !== null) {
        formatted[row[1]] = row[2];
      }
    });

    return formatted;
  });

  const categories = Object.keys(formattedData[0]).filter(
    (key) => key !== 'name'
  );
  const series = formattedData.map((item) => {
    if (item.name.includes('Nguồn')) {
      return {
        ...item,
      };
    } else {
      return {
        name: item.name,
        values: categories.map((category) => item[category] || null),
      };
    }
  });
  return { categories, series };
};

function calculateTotals(arr) {
  const totalUPS = arr.length;
  const totals = {
    '% tải': 0,
    Runtime: 0,
    'V out (Phase L1)': 0,
    'V out (Phase L2)': 0,
    'V out (Phase L3)': 0,
  };

  // Duyệt qua từng hotspot và cộng các giá trị vào totals
  arr.forEach((hotspot) => {
    totals['% tải'] += hotspot.data['% tải'];
    totals['Runtime'] += hotspot.data['Runtime'];
    totals['V out (Phase L1)'] += hotspot.data['V out (Phase L1)'];
    totals['V out (Phase L2)'] += hotspot.data['V out (Phase L2)'];
    totals['V out (Phase L3)'] += hotspot.data['V out (Phase L3)'];
  });

  totals['Average % tải'] = totals['% tải'] / totalUPS;
  totals['Average Runtime'] = totals['Runtime'] / totalUPS;
  totals['Average V out (Phase L1)'] = totals['V out (Phase L1)'] / totalUPS;
  totals['Average V out (Phase L2)'] = totals['V out (Phase L2)'] / totalUPS;
  totals['Average V out (Phase L3)'] = totals['V out (Phase L3)'] / totalUPS;

  return totals;
}

export const NOCChart = ({ chartData }) => {
  const upsData = formatUPSData(chartData);

  const otherData = getChartData(chartData);

  const totalAverageLoadPercentage = calculateTotals(upsData);

  const centeredValueAverageVout =
    (totalAverageLoadPercentage['Average V out (Phase L1)'] +
      totalAverageLoadPercentage['Average V out (Phase L2)'] +
      totalAverageLoadPercentage['Average V out (Phase L3)']) /
    3;

  const inrowData = otherData?.series?.filter((item) =>
    item.name.includes('INROW')
  );

  const powerData = otherData?.series?.filter((item) =>
    item.name.includes('Nguồn')
  );

  return (
    <div className="noc-chart">
      <CRow className="pt-5">
        <CCol md={6} xs={12}>
          <h2 className="text-center pt-3 chart_title">UPS</h2>
          <CRow>
            <CCol xs={4}>
              <RingGaugeChart
                value={Math.round(totalAverageLoadPercentage['Average % tải'])}
                width="100%"
              />
            </CCol>
            <CCol xs={4}>
              <GaugeChart
                value={Math.round(
                  totalAverageLoadPercentage['Average Runtime']
                )}
                name="Average runtime"
                height="350px"
                width="100%"
              />
            </CCol>
            <CCol xs={4}>
              <SmoothLineChart
                value={[170, 180, 150, 220, 160, 162]}
                label={['L1', 'L2', 'L3', 'l4', 'l5', 'l6']}
                centeredValue={Math.round(centeredValueAverageVout)}
                height="350px"
                name="Average output voltage"
                width="100%"
              />
            </CCol>
          </CRow>
          <CRow className="align-items-center" style={{ gap: '10px' }}>
            <CCol xs={6}>
              <PieChart
                label={['L1', 'L2', 'L3']}
                data={[
                  {
                    value: Math.round(
                      totalAverageLoadPercentage['V out (Phase L1)']
                    ),
                    name: 'L1',
                  },
                  {
                    value: Math.round(
                      totalAverageLoadPercentage['V out (Phase L2)']
                    ),
                    name: 'L2',
                  },
                  {
                    value: Math.round(
                      totalAverageLoadPercentage['V out (Phase L3)']
                    ),
                    name: 'L3',
                  },
                ]}
                width="100%"
              />
            </CCol>
            <CCol xs={6}>
              <VerticalBarChartNOC
                data={[
                  Math.round(totalAverageLoadPercentage['V out (Phase L1)']),
                  Math.round(totalAverageLoadPercentage['V out (Phase L2)']),
                  Math.round(totalAverageLoadPercentage['V out (Phase L3)']),
                ]}
                name="V out"
                width="100%"
              />
            </CCol>
          </CRow>
        </CCol>
        <CCol md={6} xs={12}>
          <CRow className="pb-3">
            <h2 className="text-center pt-3 chart_title">IN ROW</h2>
            <CCol md={12}>
              <LineChart
                label={inrowData?.map((item) => item.name)}
                data={inrowData?.map((item) => item.values)}
                width="100%"
                height="350px"
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol md={6}>
              <HorizontalBarChartNOC
                text="Load percentage"
                height="300px"
                width="100%"
                label={['L1', 'L2', 'L3']}
                data={[
                  Math.round(
                    totalAverageLoadPercentage['Average V out (Phase L1)'] / 100
                  ),
                  Math.round(
                    totalAverageLoadPercentage['Average V out (Phase L2)'] / 100
                  ),
                  Math.round(
                    totalAverageLoadPercentage['Average V out (Phase L3)'] / 100
                  ),
                ]}
                unit="%"
              />
            </CCol>
            <CCol md={6}>
              <HorizontalBarChartNOC
                label={inrowData?.map((item) => item.name)}
                data={inrowData?.map(
                  (item) => item.values[item.values.length - 1]
                )}
                height="300px"
                width="100%"
              />
            </CCol>
          </CRow>
        </CCol>
        <CCol xs={12}>
          <CRow>
            <h2 className="text-center pt-3 chart_title">Nguồn Lưới</h2>
            <CCol md={6} className="pb-3">
              <TotalPowerChart data={powerData} />
            </CCol>
            <CCol md={6}>
              <HorizontalBarChartNOC
                text="Công xuất điện max/tuần"
                height="250px"
                width="100%"
                label={powerData?.map((item) => item.name)}
                data={powerData?.map((item) => item['Công suất max/tuần'])}
                unit="kW"
              />
            </CCol>
          </CRow>
        </CCol>
      </CRow>
    </div>
  );
};
